import type {Editor} from 'grapesjs';
import type {ReactElement} from 'react';
import {useContext, useEffect, useState} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text/text';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {DEFAULT_ZOOM, ZOOM_LOWER_LIMIT, ZOOM_PERCENTAGE_MULTIPLIER, ZOOM_UPPER_LIMIT} from '@EmailMakerReact/email-maker-react.constants';
import {getCurrentZoom, fitToScreen, subscribeToZoomChange, zoomIn, zoomOut} from '@EmailMakerReact/lib/email-maker-zoom-library';
import styles from './zoom-controls.module.scss';

export function ZoomControls(): ReactElement {
  const {editor} = useContext(emailMakerContext);
  const [currentZoom, setCurrentZoom] = useState<number>(() => (editor ? getCurrentZoom(editor) : DEFAULT_ZOOM));

  useEffect(() => {
    if (!editor) {
      return undefined;
    }

    const onZoomChange = (): void => {
      setCurrentZoom(getCurrentZoom(editor));
    };

    return subscribeToZoomChange(editor, onZoomChange);
  }, [editor]);

  const createZoomHandler = (action: (editor: Editor) => void): (() => void) => {
    return (): void => {
      if (!editor) {
        return;
      }
      action(editor);
    };
  };

  const onZoomIn = createZoomHandler(zoomIn);
  const onZoomOut = createZoomHandler(zoomOut);
  const onFitToScreen = createZoomHandler(fitToScreen);

  return (
    <div className={styles.zoomPill}>
      {renderFitToScreenButton(currentZoom, onFitToScreen)}
      <Icon
        ariaLabel={window.i18next.t('pmwjs_zoom_in')}
        icon="icon-zoom-in"
        isDisabled={currentZoom >= ZOOM_UPPER_LIMIT}
        isUsedAsButton
        onClick={onZoomIn}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_16}
        type={IconType.GHOST}
      />
      <Icon
        ariaLabel={window.i18next.t('pmwjs_zoom_out')}
        icon="icon-zoom-out"
        isDisabled={currentZoom <= ZOOM_LOWER_LIMIT}
        isUsedAsButton
        onClick={onZoomOut}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_16}
        type={IconType.GHOST}
      />
      <Text className={styles.zoomLabel} size={TextSize.SM} val={`${Math.round(currentZoom * ZOOM_PERCENTAGE_MULTIPLIER)}%`} />
    </div>
  );
}

function renderFitToScreenButton(currentZoom: number, onClick: () => void): ReactElement | null {
  if (currentZoom === DEFAULT_ZOOM) {
    return null;
  }
  return (
    <Icon
      ariaLabel={window.i18next.t('pmwjs_fit_to_screen')}
      icon="icon-fit-to-screen"
      isUsedAsButton
      onClick={onClick}
      shape={IconShape.SQUARE}
      size={IconSize.SIZE_ICON_16}
      type={IconType.GHOST}
    />
  );
}
